import { useEffect, useRef, useState } from "react";

import { CheckIcon, PartyPopperIcon } from "lucide-react";

import { Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useGetJobDifficultyUrgencies } from "@/hooks/queries/useGetJobDifficultyUrgencies";
import { cn } from "@/lib/utils";
import { JobInternalConfig } from "@/types";

function useCompletedCount(jobInternalConfig: JobInternalConfig) {
  const { data: jobDifficultyUrgencies, isFetched } =
    useGetJobDifficultyUrgencies();

  const checks = [
    jobInternalConfig.ownerId !== null,
    jobInternalConfig.category !== null,
    (jobInternalConfig.voiceAiStaffingDynamicQuestions?.length ?? 0) >= 3,
    !!jobDifficultyUrgencies?.[0],
    !!jobInternalConfig.autoProfileRatingRubric,
    jobInternalConfig.isWorkableJobPosted,
    jobInternalConfig.isSponsoredOnIndeed,
  ];

  return {
    completedCount: checks.filter(Boolean).length,
    total: checks.length,
    isDataSettled: isFetched,
  };
}

export const SetupCompletionTracker = ({
  jobInternalConfig,
}: {
  jobInternalConfig: JobInternalConfig;
}) => {
  /* Helpers */
  const { completedCount, total, isDataSettled } =
    useCompletedCount(jobInternalConfig);
  const percentage = Math.round((completedCount / total) * 100);
  const isComplete = completedCount >= total;

  /* Celebration modal on completion */
  const hasSettledRef = useRef(false);
  const prevCompleteRef = useRef(false);
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    if (!isDataSettled) return;

    if (!hasSettledRef.current) {
      hasSettledRef.current = true;
      prevCompleteRef.current = isComplete;
      return;
    }
    if (isComplete && !prevCompleteRef.current) {
      setShowCelebration(true);
    }
    prevCompleteRef.current = isComplete;
  }, [isComplete, isDataSettled]);

  /* Render */
  const render = () => {
    return (
      <Row className="items-center gap-5 px-1 py-2">
        <Text variant="header" className="shrink-0">
          Progress
        </Text>
        <div className="relative flex-1 py-2">
          <div className="h-1.5 rounded-full bg-slate-100" />
          <div
            className="absolute left-0 top-2 h-1.5 rounded-full bg-primary-700 transition-all"
            style={{ width: `${String(percentage)}%` }}
          />
          {Array.from({ length: total }, (_, i) => {
            const position = ((i + 1) / total) * 100;
            const filled = i < completedCount;
            return (
              <div
                key={i}
                className={cn(
                  "absolute top-1/2 flex h-5 w-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2",
                  filled
                    ? "border-primary-700 bg-primary-700"
                    : "border-slate-300 bg-white",
                )}
                style={{
                  left: `${String(position)}%`,
                }}
              >
                {filled && <CheckIcon className="h-3 w-3 text-white" />}
              </div>
            );
          })}
        </div>
        <Text variant="header" className="shrink-0">
          {percentage}%
        </Text>
      </Row>
    );
  };

  return (
    <>
      {render()}

      <SetupCompletionCelebrationModal
        open={showCelebration}
        onOpenChange={setShowCelebration}
      />
    </>
  );
};

const SetupCompletionCelebrationModal = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="text-center sm:max-w-md">
        <DialogHeader className="sm:text-center">
          <Row className="justify-center">
            <PartyPopperIcon className="h-10 w-10 text-primary-700" />
          </Row>
          <DialogTitle className="text-center text-xl">Woohoo!</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          Thank you for filling out the job configuration! Your future self and
          staffers thank you for the hard work.
        </p>
        <p className="text-sm text-slate-600">Happy wrangling :)</p>
        <DialogFooter className="sm:justify-center">
          <Button onClick={() => { onOpenChange(false); }}>
            Let&apos;s go!
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
